import { app, getAuthInstance } from '@/lib/firebase';
import type { Auth, User, ConfirmationResult } from 'firebase/auth';

const FIREBASE_API_KEY =
  import.meta.env.VITE_FIREBASE_API_KEY ?? 'AIzaSyDKL9SxUpj-jvTL7pi2uObxDSrI3c0pkvY';

export const clearBackupAuthState = (): void => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem('firebase-auth-uid-backup');
    sessionStorage.removeItem('firebase-auth-email-backup');
    sessionStorage.removeItem('beta-rest-auth');
  } catch {}
};

const ensureAuthSettings = (auth: Auth): void => {
  try {
    const targets = [(auth as any), (auth as any)?._delegate].filter(Boolean);
    targets.forEach((authAny) => {
      if (!authAny.settings) {
        try {
          Object.defineProperty(authAny, 'settings', {
            value: {},
            writable: true,
            configurable: true,
          });
        } catch {
          authAny.settings = {};
        }
      }
      if (!authAny.settings) authAny.settings = {};
      if (typeof authAny.settings.appVerificationDisabledForTesting === 'undefined') {
        authAny.settings.appVerificationDisabledForTesting = false;
      }
    });
  } catch {}
};

const isInvalidAppCheckError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('auth/firebase-app-check-token-is-invalid');
};

const signInWithPasswordRestFallback = async (email: string, password: string): Promise<User> => {
  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(FIREBASE_API_KEY)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        returnSecureToken: true,
      }),
    },
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = payload?.error?.message ? `auth/${String(payload.error.message).toLowerCase()}` : 'auth/network-request-failed';
    throw new Error(code);
  }

  const fallbackUser = {
    uid: payload.localId ?? '',
    email: payload.email ?? email,
  } as User;

  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('firebase-auth-uid-backup', fallbackUser.uid);
      sessionStorage.setItem('firebase-auth-email-backup', fallbackUser.email || '');
      sessionStorage.setItem('beta-rest-auth', '1');
    }
  } catch {}

  return fallbackUser;
};

export async function signInWithEmailPassword(
  email: string,
  password: string,
): Promise<User> {
  const auth = await getAuthInstance();
  if (!auth) throw new Error('Auth not available');
  
  // Explicitly set persistence before sign-in (critical for iOS)
  try {
    const { setPersistence, browserLocalPersistence } = await import('firebase/auth');
    await setPersistence(auth, browserLocalPersistence);
    console.info('[auth] Persistence explicitly set before sign-in');
  } catch (err) {
    console.warn('[auth] Failed to set persistence before sign-in:', err);
  }
  
  // Wait for auth to be fully ready (persistence set, etc.)
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // Ensure the internal `settings` object exists on the Auth instance. Some
  // SDK environments (or mismatched module instances) may leave this unset
  // and RecaptchaVerifier accesses `auth.settings.appVerificationDisabledForTesting`.
  ensureAuthSettings(auth);
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    if (!isInvalidAppCheckError(error)) {
      throw error;
    }
    console.warn('[auth] Firebase SDK sign-in blocked by App Check, using REST fallback');
    return await signInWithPasswordRestFallback(email, password);
  }
  
  console.info('[auth] User signed in:', cred.user.uid);
  
  // iOS Safari fallback: store user ID in sessionStorage as backup
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('firebase-auth-uid-backup', cred.user.uid);
      sessionStorage.setItem('firebase-auth-email-backup', cred.user.email || '');
      console.info('[auth] Backup auth data stored in sessionStorage for iOS');
    }
  } catch (err) {
    console.warn('[auth] Failed to store backup auth data:', err);
  }
  
  // Give iOS time to write to IndexedDB
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  return cred.user;
}

export async function signOut(): Promise<void> {
  const auth = await getAuthInstance();
  if (!auth) {
    clearBackupAuthState();
    return;
  }
  const { signOut } = await import('firebase/auth');
  await signOut(auth);
  clearBackupAuthState();
}

// Try the branded SendGrid email endpoint (logo + rich HTML). Returns true if it
// accepted the request. Any failure (endpoint not deployed, SendGrid key unset,
// dev/localhost with no hosting rewrite) returns false so the caller can fall back
// to Firebase's plain default — reset/verify always works either way.
async function trySendBrandedAuthEmail(email: string, type: 'reset' | 'verify'): Promise<boolean> {
  try {
    const { getAppCheckToken } = await import('@/lib/firebase');
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const appCheckToken = await getAppCheckToken().catch(() => null);
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
    const res = await fetch('/send-auth-email', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, type }),
    });
    return res.ok;
  } catch (err) {
    console.warn('[auth] branded auth email failed; falling back to Firebase default:', err);
    return false;
  }
}

export async function sendPasswordReset(email: string): Promise<void> {
  if (await trySendBrandedAuthEmail(email, 'reset')) return;
  const auth = await getAuthInstance();
  if (!auth) throw new Error('Auth not available');
  const { sendPasswordResetEmail } = await import('firebase/auth');
  await sendPasswordResetEmail(auth, email);
}

export async function createUserWithEmailPassword(
  email: string,
  password: string,
): Promise<User> {
  const auth = await getAuthInstance();
  if (!auth) throw new Error('Auth not available');
  const { createUserWithEmailAndPassword } = await import('firebase/auth');
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  
  // Wait for persistence to complete (increased to 1s for mobile)
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return cred.user;
}

export async function startPhoneSignIn(
  phoneNumber: string,
  recaptchaContainerId = 'recaptcha',
  tenantId?: string,
): Promise<ConfirmationResult> {
  const auth = await getAuthInstance();
  if (!auth) throw new Error('Auth not available');

  // Optionally set tenant for multi-tenant projects
  if (tenantId) {
    try {
      (auth as any).tenantId = tenantId;
    } catch {}
  }

  ensureAuthSettings(auth);

  const authModule = await import('firebase/auth');
  const { RecaptchaVerifier, signInWithPhoneNumber } = authModule;

  let verifier: InstanceType<typeof RecaptchaVerifier> | null = null;

  try {
    // Ensure container exists and is completely clean
    if (typeof document !== 'undefined') {
      let container = document.getElementById(recaptchaContainerId);
      if (container) {
        // Remove and recreate to clear any reCAPTCHA state
        container.remove();
      }
      container = document.createElement('div');
      container.id = recaptchaContainerId;
      container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;';
      document.body.appendChild(container);
    }

    // Firebase Auth v9+ RecaptchaVerifier signature: new RecaptchaVerifier(auth, container, parameters)
    verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'invisible' });

    // Skip render() call - Firebase handles this internally when signInWithPhoneNumber is called
    // In Firebase v11+, attempting to render() after enterprise v3 is loaded causes conflicts
    console.debug('[auth] RecaptchaVerifier created (render skipped for Firebase v11+)');

    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    console.debug('[auth] signInWithPhoneNumber succeeded');

    // Wrap to use fresh auth instance on confirm
    const verificationId = (confirmation as any)?.verificationId;
    if (verificationId) {
      return {
        verificationId,
        confirm: async (code: string) => {
          const authCurrent = await getAuthInstance();
          if (!authCurrent) throw new Error('Auth not available for confirmation');
          const { PhoneAuthProvider, signInWithCredential } = await import('firebase/auth');
          const credential = PhoneAuthProvider.credential(verificationId, code);
          const cred = await signInWithCredential(authCurrent, credential);
          return cred;
        },
      } as unknown as ConfirmationResult;
    }
    return confirmation;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error('[auth] Phone sign-in failed', e);
    throw new Error('Phone sign-in failed: ' + msg);
  } finally {
    try {
      verifier?.clear();
    } catch {}
  }
}

export async function confirmPhoneCode(confirmation: ConfirmationResult, code: string): Promise<User> {
  const result = await confirmation.confirm(code);
  return result.user;
}

export async function signInWithGoogle(): Promise<User> {
  const auth = await getAuthInstance();
  if (!auth) throw new Error('Auth not available');
  const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  const result = await signInWithPopup(auth, provider);
  
  // Wait for persistence to complete (increased to 1s for mobile)
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return result.user;
}

export default {
  signInWithEmailPassword,
  sendPasswordReset,
  signOut,
  startPhoneSignIn,
  confirmPhoneCode,
  signInWithGoogle,
};
